package scroll

import common.dto.Vec3d
import kotlinx.serialization.Serializable

/**
 * Represents a keyframe that defines the elements and their positions at a certain scroll
 * [progress]. The frames in-between are interpolated (linearly) between the keyframes.
 *
 * The [elements] are a list of elements that are present in the scene at this keyframe. By default,
 * we assume that elements are only present in *this* specific keyframe, i.e. they appear before
 * and disappear after this keyframe.
 * If an element should be present in multiple keyframes, it
 * must be persisted by specifying an identifier on the [ScrollElement].
 *
 * @param progress The scroll progress at which this keyframe is reached (in viewport height units).
 * @param globalTransform The global transformation that is applied to all elements in this keyframe.
 * @param globalRotation The global rotation that is applied to all elements in this keyframe.
 *
 * // todo maybe some kind of additional animation that can be applied to certain elements?
 *      or some property for applying "global" animations on a keyframe-basis?
 */
// todo dynamically add headings to html for each keyframe?
// todo ability to specify a list of elements that should be persisted without modification from previous keyframes?
@Serializable
data class ScrollKeyframe(
    val progress: ClosedRange<Double>,
    val elements: List<ScrollElement>,
    val globalTransform: Vec3d = Vec3d(),
    val globalRotation: Vec3d = Vec3d()
)

/**
 * Convenience function to create a [ScrollKeyframe] with a single scroll progress value.
 */
fun ScrollKeyframe(
    progress: Double,
    elements: List<ScrollElement> = emptyList(),
    globalTransform: Vec3d = Vec3d(),
    globalRotation: Vec3d = Vec3d()
) = ScrollKeyframe(progress..progress, elements, globalTransform, globalRotation)